// pages/master/role/ManagementRoleDialog.jsx
'use client';

import { useState, useEffect, useMemo } from 'react';
import { Box, CircularProgress, Tabs, Tab, Button, Badge } from '@mui/material';
import PropTypes from 'prop-types';

import FormDialog from '../../../components/FormDialog';
import { useNotifier } from '../../../components/Notifier';
import { useLanguage } from '../../../utils/i18n';

import { getRolesModuleByName, getRolesCompanyList, getRolesVehicleList, searchVehicleForRoles } from '../../../stores/roleModuleStore';

import MenuManage from './content/MenuManage';
import CompanyManage from './content/CompanyManage';
import VehicleManage from './content/VehicleManage';

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`management-tabpanel-${index}`}
      aria-labelledby={`management-tab-${index}`}
      style={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}
      {...other}
    >
      {value === index && <Box sx={{ p: 3, flexGrow: 1 }}>{children}</Box>}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired
};

function a11yProps(index) {
  return {
    id: `management-tab-${index}`,
    'aria-controls': `management-tabpanel-${index}`
  };
}

const ManagementRoleDialog = ({ open, roleName, onClose, roleParentName, initialTab = 0 }) => {
  const notify = useNotifier();
  const { t } = useLanguage();

  const [tabValue, setTabValue] = useState(initialTab);

  useEffect(() => {
    if (open) {
      setTabValue(initialTab);
    }
  }, [open, initialTab]);

  const [loading, setLoading] = useState(false);

  const [dataModuleByName, setDataModuleByName] = useState(null);
  const [dataModuleList, setDataModuleList] = useState([]);
  const [dataCompanyList, setDataCompanyList] = useState([]);
  const [dataVehicleList, setDataVehicleList] = useState([]);
  const [dataSearchVehicle, setDataSearchVehicle] = useState([]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const companyCount = useMemo(() => {
    if (!dataCompanyList) return 0;
    if (Array.isArray(dataCompanyList) && dataCompanyList.length > 0) {
      return (dataCompanyList[0].company_id || dataCompanyList[0].company || []).length;
    }
    return (dataCompanyList.company_id || dataCompanyList.company || []).length;
  }, [dataCompanyList]);

  const vehicleCount = useMemo(() => {
    let rawIds = [];
    if (!dataVehicleList) return 0;
    if (Array.isArray(dataVehicleList) && dataVehicleList.length > 0) {
      rawIds = dataVehicleList[0].vehicle_id || dataVehicleList[0].vehicle || [];
    } else {
      rawIds = dataVehicleList.vehicle_id || dataVehicleList.vehicle || [];
    }

    if (!dataSearchVehicle || dataSearchVehicle.length === 0) return 0;

    const uniqueIds = [...new Set(rawIds)];
    return uniqueIds.filter((id) => dataSearchVehicle.some((v) => String(v.id) === String(id))).length;
  }, [dataVehicleList, dataSearchVehicle]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [resModuleByName, resCompanyList, resVehicleList] = await Promise.all([
        getRolesModuleByName(roleName).catch(() => null),
        getRolesCompanyList(roleName).catch(() => []),
        getRolesVehicleList(roleName).catch(() => [])
      ]);

      setDataModuleByName(resModuleByName);
      setDataModuleList(resModuleByName?.data_modules ?? []);

      const companies = resCompanyList?.data ?? resCompanyList ?? [];
      setDataCompanyList(companies);

      setDataVehicleList(resVehicleList?.data ?? resVehicleList ?? []);
      let companyIds = [];

      if (Array.isArray(companies)) {
        companies.forEach((item) => {
          if (Array.isArray(item.company_id)) {
            companyIds = [...companyIds, ...item.company_id];
          }
        });
      } else if (companies && Array.isArray(companies.company_id)) {
        companyIds = [...companies.company_id];
      }

      companyIds = [...new Set(companyIds)].filter(Boolean);

      if (companyIds.length > 0) {
        const resSearchVehicle = await searchVehicleForRoles(companyIds).catch(() => []);
        setDataSearchVehicle(resSearchVehicle?.data ?? resSearchVehicle ?? []);
      }
    } catch (error) {
      console.error('Error fetching role management details:', error);
      notify({ message: t('masterRole.failedLoadData', 'Failed to load management role details'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleName]);

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={`${t('masterRole.managementRole', 'Management Role')}: ${roleName || '-'}`}
      maxWidth="lg"
      fullScreenMode={true}
      actions={
        <>
          <Button onClick={onClose} disableRipple disableElevation variant="outlined">
            {t('general.close', 'Close')}
          </Button>
        </>
      }
    >
      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 150px)' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3, pt: 1, position: 'sticky', top: -24, zIndex: 10, bgcolor: '#fff' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="Management Role Tabs"
            textColor="primary"
            indicatorColor="primary"
            variant="fullWidth"
          >
            <Tab label={t('masterRole.menu', 'Menu')} {...a11yProps(0)} sx={{ fontWeight: 'bold', textTransform: 'none' }} />
            <Tab
              label={
                <Badge badgeContent={companyCount} color="primary" sx={{ '& .MuiBadge-badge': { right: -15, top: 0 } }}>
                  {t('masterRole.company', 'Company')}
                </Badge>
              }
              {...a11yProps(1)}
              sx={{ fontWeight: 'bold', textTransform: 'none' }}
            />
            <Tab
              label={
                <Badge badgeContent={vehicleCount} color="primary" sx={{ '& .MuiBadge-badge': { right: -15, top: 0 } }}>
                  {t('masterRole.vehicle', 'Vehicle')}
                </Badge>
              }
              {...a11yProps(2)}
              sx={{ fontWeight: 'bold', textTransform: 'none' }}
            />
          </Tabs>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1, height: '50vh' }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            <CustomTabPanel value={tabValue} index={0}>
              <MenuManage
                roleName={roleName}
                dataModuleByName={dataModuleByName}
                dataModuleList={dataModuleList}
                roleParentName={roleParentName}
                onClose={onClose}
              />
            </CustomTabPanel>
            <CustomTabPanel value={tabValue} index={1}>
              <CompanyManage roleName={roleName} dataCompanyList={dataCompanyList} onRefresh={fetchAllData} />
            </CustomTabPanel>
            <CustomTabPanel value={tabValue} index={2}>
              <VehicleManage
                roleName={roleName}
                dataVehicleList={dataVehicleList}
                dataSearchVehicle={dataSearchVehicle}
                onRefresh={fetchAllData}
              />
            </CustomTabPanel>
          </Box>
        )}
      </Box>
    </FormDialog>
  );
};

export default ManagementRoleDialog;
